/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './TrustedContactsTable.scss';
import { camelCase, isNil, kebabCase } from 'lodash';
import { object } from 'prop-types';
import React, { Fragment } from 'react';

import AccountDisplay from 'components/shared/AccountDisplay/AccountDisplay';
import Optioner from 'components/shared/Optioner/Optioner';
import AddTrustedContacts from 'components/layouts/Preferences/TrustedContacts/TrustedContactsModal/TrustedContactsModal';
import TrustedContactsTableActionModal from 'components/layouts/Preferences/TrustedContacts/TrustedContactsTableActionModal/TrustedContactsTableActionModal';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { isActiveAdvisory, isActivePlus } from 'services/Products';

const TRUSTED_CONTACT = 'Trusted Contact';
const PHONE_NUMBER = 'Phone Number';
const EMAIL_ADDRESS = 'Email Address';
const RELATIONSHIP = 'Relationship';
const ACTIONS = 'Actions';
const FIRST_NAME = 'firstName';
const LAST_NAME = 'lastName';

const columns = [
  TRUSTED_CONTACT,
  PHONE_NUMBER,
  EMAIL_ADDRESS,
  RELATIONSHIP,
  ACTIONS
];

const ActionsButton = ({ account, contact }) => {
  const { isEditable } = account;
  return <Fragment>
    {isEditable && <td className="trusted-contacts-table-body-cell action-cell">
      <TrustedContactsTableActionModal account={account} contact={contact} />
    </td>}
  </Fragment>;
};

const Contact = ({ firstName, lastName }) => (
  <Fragment>{`${firstName} ${lastName}`}</Fragment>
);

const Number = ({ phoneNumber }) => {
  const { areaCode, exchange, localNumber } = phoneNumber || {};
  return <Fragment>{`(${areaCode}) ${exchange}-${localNumber}`}</Fragment>;
};

const Email = ({ email }) => <Fragment>{email.address}</Fragment>;

const RelationshipToUser = ({ relationship }) => (
  <Fragment>{relationship}</Fragment>
);

const TrustedContactWithPermissions = WithUserPermissions(Contact);
const PhoneNumberWithPermissions = WithUserPermissions(Number);
const EmailAddressWithPermissions = WithUserPermissions(Email);
const RelationshipWithPermissions = WithUserPermissions(RelationshipToUser);

const TrustedContact = ({ firstName, lastName }) => (
  <td className="trusted-contacts-table-body-cell name">
    <TrustedContactWithPermissions
      firstName={firstName}
      lastName={lastName}
      shouldMask
    />
  </td>
);

const PhoneNumber = ({ phoneNumber }) => {
  return (
    <td className="trusted-contacts-table-body-cell phone-number-cell">
      <PhoneNumberWithPermissions phoneNumber={phoneNumber} shouldMask />
    </td>
  );
};

const EmailAddress = ({ email }) => (
  <td className="trusted-contacts-table-body-cell email-address-cell">
    <EmailAddressWithPermissions email={email} shouldMask />
  </td>
);

const Relationship = ({ relationship }) => (
  <td className="trusted-contacts-table-body-cell relationship-cell">
    <RelationshipWithPermissions relationship={relationship} shouldMask />
  </td>
);

const AccountGroupHeader = ({ account }) => {
  const { groupingClassName, name } = account;

  return (
    <div
      className={`trusted-contacts-group-header-container ${groupingClassName}`}>
      <AccountDisplay
        account={name}
        withType={true}
        withLink={
          !isActiveAdvisory(name.productType) && !isActivePlus(name.productType)
        }
      />
    </div>
  );
};

const TrustedContactsTableHeader = () => (
  <tr>
    {columns.map((name) => {
      const colName = name === ACTIONS ? '' : name;
      return (
        <th
          scope="col"
          className={`trusted-contacts-table-header-cell e2e-${camelCase(
            name
          )} trusted-contacts-table-${kebabCase(name)}`}
          key={name}>
          {colName}
        </th>
      );
    })}
  </tr>
);

const TrustedContactsTableBody = ({ account }) => {
  const { trustedContacts, isEditable } = account;
  const hasTrustedContacts = Boolean(trustedContacts);
  return hasTrustedContacts ? (
    <HasTrustedContacts account={account} />
  ) : (
    <Fragment>
        {isEditable ? <NoTrustedContactRow account={account} /> : <NoTrustedContactsOnFileRow />}
      <NoTrustedContactRow account={account} />
    </Fragment>
  );
};

const HasTrustedContacts = ({ account }) => {
  const [contactOne, contactTwo = null] = [...account.trustedContacts];
  const trustedContacts = [contactOne, contactTwo];

  return (
    <Fragment>
      {trustedContacts.map((contact, key) =>
        isNil(contact) ? (
          <NoTrustedContactRow key={key} account={account} />
        ) : (
          <TrustedContactRow key={key} account={account} contact={contact} />
        )
      )}
    </Fragment>
  );
};

HasTrustedContacts.propTypes = {
  account: object
};

const TrustedContactRow = ({ account, contact }) => (
  <tr>
    {columns.map((column, key) => {
      const selection = camelCase(column);
      return (
        <Optioner key={key} val={selection}>
          {{
            trustedContact: (
              <TrustedContact
                firstName={contact[FIRST_NAME]}
                lastName={contact[LAST_NAME]}
              />
            ),
            phoneNumber: <PhoneNumber phoneNumber={contact[selection]} />,
            emailAddress: <EmailAddress email={contact[selection]} />,
            relationship: <Relationship relationship={contact[selection]} />,
            actions: <ActionsButton account={account} contact={contact} />
          }}
        </Optioner>
      );
    })}
  </tr>
);

TrustedContactRow.propTypes = {
  account: object.isRequired,
  contact: object.isRequired
};

const NoTrustedContactRow = ({ account }) => {
  const { isEditable } = account;
  return <tr>
      <td colSpan={5} className="trusted-contacts-table-body-cell">
        {isEditable &&
          <AddTrustedContacts
          account={account}
          contact={null}
          linkName="Add Trusted Contact"
          isAddView={true}
          modalTitle="Add Trusted Contact"
        />
        }
      </td>
    </tr>
};

const NoTrustedContactsOnFileRow = () => {
    return <tr>
        <td colSpan={5} className="trusted-contacts-table-body-cell">
            <div className="trusted-contacts-copy-container">
                No Trusted Contact(s) on File
            </div>
        </td>
    </tr>
};

const TrustedContactsTable = ({ account }) => (
  <div className="trusted-contacts-table-container">
    <AccountGroupHeader account={account} />
    <table className="trusted-contacts-table">
      <thead className="trusted-contacts-table-header">
        <TrustedContactsTableHeader />
      </thead>
      <tbody className="trusted-contacts-table-body">
        <TrustedContactsTableBody account={account} />
      </tbody>
    </table>
  </div>
);

TrustedContactsTable.propTypes = {
  account: object.isRequired
};

export default TrustedContactsTable;
